import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    X, Save, Pin, Lock, Users, Globe, Bold, Italic, Code, List, ListOrdered,
    Link2, Hash, CheckSquare, Loader2, Paperclip, ChevronDown,
    AlertTriangle, Type, Heading1, Heading2, Heading3, Plus, Smile,
    Maximize2, Minimize2
} from 'lucide-react';
import { uploadNoteAttachment } from '../api/notes';

// Emoji categories for the picker
const EMOJI_CATEGORIES = [
    {
        name: 'Smileys',
        emojis: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😋', '😎', '🤩', '🥳', '😏', '🤔', '🤗', '😬', '😮', '🙄', '😴']
    },
    {
        name: 'Gestures',
        emojis: ['👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '💪', '🤜', '🤛', '☝️', '👆', '👇', '👈', '👉', '🖐️', '✋', '👋', '🤚', '✍️', '🙏']
    },
    {
        name: 'Business',
        emojis: ['💼', '📊', '📈', '📉', '💰', '💵', '💳', '🏦', '📋', '📝', '✅', '❌', '⚠️', '🔔', '📅', '⏰', '🎯', '🏆', '⭐', '💡', '🔑', '📌', '📎', '🗂️', '📁', '📂']
    },
    {
        name: 'Status',
        emojis: ['✅', '❌', '⏳', '⌛', '🔄', '🚀', '🎉', '🔥', '💯', '⚡', '❗', '❓', '💬', '📣', '🔒', '🔓', '🛑', '✨', '🌟', '💎', '🏁', '🚧']
    },
    {
        name: 'Hearts',
        emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '💕', '💞', '💓', '💗', '💖', '💘', '💝']
    }
];

const VISIBILITY_OPTIONS = [
    { value: 'PRIVATE', label: 'Private', icon: Lock, description: 'Only you can see' },
    { value: 'PROTECTED', label: 'Team', icon: Users, description: 'Team members can see' },
    { value: 'PUBLIC', label: 'Public', icon: Globe, description: 'Anyone can see' }
];

// Parse a line to determine its type and content
const parseLine = (line) => {
    if (line.match(/^-\s*\[x\]\s*/i)) {
        return { type: 'task', checked: true, content: line.replace(/^-\s*\[x\]\s*/i, '') };
    }
    if (line.match(/^-\s*\[\s*\]\s*/)) {
        return { type: 'task', checked: false, content: line.replace(/^-\s*\[\s*\]\s*/, '') };
    }
    if (line.startsWith('### ')) {
        return { type: 'h3', content: line.slice(4) };
    }
    if (line.startsWith('## ')) {
        return { type: 'h2', content: line.slice(3) };
    }
    if (line.startsWith('# ')) {
        return { type: 'h1', content: line.slice(2) };
    }
    if (line.startsWith('- ')) {
        return { type: 'bullet', content: line.slice(2) };
    }
    const numberedMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numberedMatch) {
        return { type: 'numbered', number: numberedMatch[1], content: numberedMatch[2] };
    }
    if (line.startsWith('#') && !line.startsWith('# ')) {
        return { type: 'tag', content: line };
    }
    if (line.trim() === '') {
        return { type: 'empty', content: '' };
    }
    return { type: 'paragraph', content: line };
};

// Render inline formatting
const RenderInline = ({ text }) => {
    if (!text) return null;

    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
        const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
        if (boldMatch) {
            parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
            remaining = remaining.slice(boldMatch[0].length);
            continue;
        }

        const italicMatch = remaining.match(/^\*([^*]+?)\*/);
        if (italicMatch) {
            parts.push(<em key={key++} className="italic">{italicMatch[1]}</em>);
            remaining = remaining.slice(italicMatch[0].length);
            continue;
        }

        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
            parts.push(
                <code key={key++} className="px-1.5 py-0.5 bg-white/10 rounded text-[#ff4d4d] text-[0.9em] font-mono">
                    {codeMatch[1]}
                </code>
            );
            remaining = remaining.slice(codeMatch[0].length);
            continue;
        }

        const tagMatch = remaining.match(/^#([\w-]+)/);
        if (tagMatch) {
            parts.push(
                <span key={key++} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#ff4d4d]/10 text-[#ff4d4d] border border-[#ff4d4d]/20">
                    #{tagMatch[1]}
                </span>
            );
            remaining = remaining.slice(tagMatch[0].length);
            continue;
        }

        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
            parts.push(
                <a
                    key={key++}
                    href={linkMatch[2]}
                    className="text-sky-400 hover:text-sky-300 underline decoration-sky-400/30 hover:decoration-sky-300 transition"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {linkMatch[1]}
                </a>
            );
            remaining = remaining.slice(linkMatch[0].length);
            continue;
        }

        parts.push(remaining[0]);
        remaining = remaining.slice(1);
    }

    return <>{parts}</>;
};

// Individual editable block component
const EditableBlock = ({
    line,
    index,
    isActive,
    onFocus,
    onChange,
    onKeyDown,
    onToggleTask,
    blockRef
}) => {
    const parsed = parseLine(line);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isActive && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isActive]);

    const handleInput = (e) => {
        let newValue = e.target.value;

        if (parsed.type === 'task') {
            const prefix = parsed.checked ? '- [x] ' : '- [ ] ';
            onChange(index, prefix + newValue);
        } else if (parsed.type === 'h1') {
            onChange(index, '# ' + newValue);
        } else if (parsed.type === 'h2') {
            onChange(index, '## ' + newValue);
        } else if (parsed.type === 'h3') {
            onChange(index, '### ' + newValue);
        } else if (parsed.type === 'bullet') {
            onChange(index, '- ' + newValue);
        } else if (parsed.type === 'numbered') {
            onChange(index, parsed.number + '. ' + newValue);
        } else {
            onChange(index, newValue);
        }
    };

    const commonInputClass = "w-full bg-transparent border-none outline-none resize-none";

    // Task (checkbox) block
    if (parsed.type === 'task') {
        return (
            <div
                ref={blockRef}
                className={`group flex items-start gap-3 py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleTask(index);
                    }}
                    className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        parsed.checked
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-white/30 hover:border-emerald-400'
                    }`}
                >
                    {parsed.checked && (
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                </button>
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 text-white ${parsed.checked ? 'line-through opacity-60' : ''}`}
                        placeholder="Task description..."
                    />
                ) : (
                    <span className={`flex-1 text-white ${parsed.checked ? 'line-through opacity-60' : ''}`}>
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-white/30">Task description...</span>}
                    </span>
                )}
            </div>
        );
    }

    // Heading blocks
    if (parsed.type === 'h1') {
        return (
            <div
                ref={blockRef}
                className={`py-2 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-2xl font-bold text-white`}
                        placeholder="Heading 1"
                    />
                ) : (
                    <h1 className="text-2xl font-bold text-white">
                        {parsed.content || <span className="text-white/30">Heading 1</span>}
                    </h1>
                )}
            </div>
        );
    }

    if (parsed.type === 'h2') {
        return (
            <div
                ref={blockRef}
                className={`py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-xl font-semibold text-white`}
                        placeholder="Heading 2"
                    />
                ) : (
                    <h2 className="text-xl font-semibold text-white">
                        {parsed.content || <span className="text-white/30">Heading 2</span>}
                    </h2>
                )}
            </div>
        );
    }

    if (parsed.type === 'h3') {
        return (
            <div
                ref={blockRef}
                className={`py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-lg font-semibold text-white/90`}
                        placeholder="Heading 3"
                    />
                ) : (
                    <h3 className="text-lg font-semibold text-white/90">
                        {parsed.content || <span className="text-white/30">Heading 3</span>}
                    </h3>
                )}
            </div>
        );
    }

    // Bullet list
    if (parsed.type === 'bullet') {
        return (
            <div
                ref={blockRef}
                className={`flex items-start gap-3 py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 text-white/80`}
                        placeholder="List item"
                    />
                ) : (
                    <span className="flex-1 text-white/80">
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-white/30">List item</span>}
                    </span>
                )}
            </div>
        );
    }

    // Numbered list
    if (parsed.type === 'numbered') {
        return (
            <div
                ref={blockRef}
                className={`flex items-start gap-3 py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                <span className="text-white/40 font-medium min-w-[1.5rem]">{parsed.number}.</span>
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 text-white/80`}
                        placeholder="List item"
                    />
                ) : (
                    <span className="flex-1 text-white/80">
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-white/30">List item</span>}
                    </span>
                )}
            </div>
        );
    }

    // Tag line
    if (parsed.type === 'tag') {
        return (
            <div
                ref={blockRef}
                className={`py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={(e) => onChange(index, e.target.value)}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-white/80`}
                        placeholder="#tag"
                    />
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <RenderInline text={parsed.content} />
                    </div>
                )}
            </div>
        );
    }

    // Empty line
    if (parsed.type === 'empty') {
        return (
            <div
                ref={blockRef}
                className={`py-1 px-2 -mx-2 rounded-lg transition-colors min-h-[28px] ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                onClick={() => onFocus(index)}
            >
                {isActive && (
                    <input
                        ref={inputRef}
                        type="text"
                        value=""
                        onChange={(e) => onChange(index, e.target.value)}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-white/80`}
                        placeholder="Type '/' for commands, or start writing..."
                    />
                )}
            </div>
        );
    }

    // Default paragraph
    return (
        <div
            ref={blockRef}
            className={`py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
            onClick={() => onFocus(index)}
        >
            {isActive ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={parsed.content}
                    onChange={(e) => onChange(index, e.target.value)}
                    onKeyDown={(e) => onKeyDown(e, index)}
                    className={`${commonInputClass} text-white/80 leading-relaxed`}
                    placeholder="Type something..."
                />
            ) : (
                <p className="text-white/80 leading-relaxed">
                    {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-white/30">Type something...</span>}
                </p>
            )}
        </div>
    );
};

// Block type menu
const BlockTypeMenu = ({ position, onSelect, onClose }) => {
    const options = [
        { type: 'paragraph', label: 'Text', icon: Type, description: 'Plain text', prefix: '' },
        { type: 'h1', label: 'Heading 1', icon: Heading1, description: 'Large heading', prefix: '# ' },
        { type: 'h2', label: 'Heading 2', icon: Heading2, description: 'Medium heading', prefix: '## ' },
        { type: 'h3', label: 'Heading 3', icon: Heading3, description: 'Small heading', prefix: '### ' },
        { type: 'bullet', label: 'Bullet List', icon: List, description: 'Simple list', prefix: '- ' },
        { type: 'numbered', label: 'Numbered List', icon: ListOrdered, description: 'Numbered list', prefix: '1. ' },
        { type: 'task', label: 'To-do', icon: CheckSquare, description: 'Task with checkbox', prefix: '- [ ] ' },
    ];

    return (
        <div
            className="absolute z-50 w-64 bg-[#0d1424] border border-white/10 rounded-xl shadow-xl overflow-hidden animate-fadeIn"
            style={{ left: position.x, top: position.y }}
        >
            <div className="p-2">
                <p className="text-xs font-medium text-white/40 px-2 py-1">BLOCKS</p>
                {options.map((opt) => (
                    <button
                        key={opt.type}
                        onClick={() => onSelect(opt.prefix)}
                        className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition text-left"
                    >
                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                            <opt.icon size={20} className="text-white/50" />
                        </div>
                        <div>
                            <div className="font-medium text-white text-sm">{opt.label}</div>
                            <div className="text-xs text-white/40">{opt.description}</div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};

// Emoji Picker component
const EmojiPicker = ({ onSelect, onClose }) => {
    const [activeCategory, setActiveCategory] = useState(0);
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    return (
        <div
            ref={pickerRef}
            className="absolute top-full left-0 mt-2 w-80 bg-[#0d1424] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-fadeIn"
        >
            {/* Category tabs */}
            <div className="flex gap-1 p-2 border-b border-white/10 overflow-x-auto">
                {EMOJI_CATEGORIES.map((cat, idx) => (
                    <button
                        key={cat.name}
                        onClick={() => setActiveCategory(idx)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition ${
                            activeCategory === idx
                                ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                                : 'text-white/40 hover:bg-white/5'
                        }`}
                    >
                        {cat.name}
                    </button>
                ))}
            </div>

            {/* Emoji grid */}
            <div className="p-2 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-8 gap-1">
                    {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                onSelect(emoji);
                                onClose();
                            }}
                            className="w-8 h-8 flex items-center justify-center text-xl hover:bg-white/5 rounded-lg transition"
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AUTOSAVE_DELAY = 1500;

const NoteEditor = ({ note, onSave, onAutosave, onClose, isSaving }) => {
    const [lines, setLines] = useState(() => {
        const content = note?.content || '';
        return content ? content.split('\n') : [''];
    });
    const [activeLineIndex, setActiveLineIndex] = useState(0);
    const [visibility, setVisibility] = useState(note?.visibility || 'PRIVATE');
    const [pinned, setPinned] = useState(note?.pinned || false);
    const [attachments, setAttachments] = useState(note?.attachments || []);
    const [isUploading, setIsUploading] = useState(false);
    const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [error, setError] = useState(null);
    const [showBlockMenu, setShowBlockMenu] = useState(false);
    const [blockMenuPosition, setBlockMenuPosition] = useState({ x: 0, y: 0 });
    const [isExpanded, setIsExpanded] = useState(false);

    // Autosave state
    const [saveStatus, setSaveStatus] = useState('saved');
    const [lastSavedContent, setLastSavedContent] = useState(note?.content || '');
    const autosaveTimerRef = useRef(null);

    const fileInputRef = useRef(null);
    const editorRef = useRef(null);
    const blockRefs = useRef([]);

    const content = lines.join('\n');

    // Autosave effect
    useEffect(() => {
        if (!onAutosave) return;

        if (content === lastSavedContent) {
            setSaveStatus('saved');
            return;
        }

        if (!content.trim()) return;

        setSaveStatus('unsaved');

        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }

        autosaveTimerRef.current = setTimeout(async () => {
            setSaveStatus('saving');
            setError(null);

            try {
                const noteData = {
                    content: content.trim(),
                    visibility,
                    pinned,
                    attachments: attachments.map(a => a.id),
                };

                await onAutosave(noteData);

                setLastSavedContent(content);
                setSaveStatus('saved');
            } catch (err) {
                console.error('Autosave failed:', err);
                setSaveStatus('error');
                setError('Autosave failed. Your changes may not be saved.');
            }
        }, AUTOSAVE_DELAY);

        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, [content, visibility, pinned, attachments, lastSavedContent, onAutosave]);

    useEffect(() => {
        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, []);

    const handleLineChange = useCallback((index, newValue) => {
        if (newValue === '/') {
            const blockEl = blockRefs.current[index];
            if (blockEl) {
                const rect = blockEl.getBoundingClientRect();
                const editorRect = editorRef.current?.getBoundingClientRect();
                if (editorRect) {
                    setBlockMenuPosition({
                        x: rect.left - editorRect.left,
                        y: rect.bottom - editorRect.top + 8
                    });
                    setShowBlockMenu(true);
                }
            }
        } else {
            setShowBlockMenu(false);
        }

        setLines(prev => {
            const newLines = [...prev];
            newLines[index] = newValue;
            return newLines;
        });
    }, []);

    const handleKeyDown = useCallback((e, index) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const currentLine = lines[index];
            const parsed = parseLine(currentLine);

            let newLinePrefix = '';
            if (parsed.type === 'task' && parsed.content.trim() !== '') {
                newLinePrefix = '- [ ] ';
            } else if (parsed.type === 'bullet' && parsed.content.trim() !== '') {
                newLinePrefix = '- ';
            } else if (parsed.type === 'numbered' && parsed.content.trim() !== '') {
                newLinePrefix = (parseInt(parsed.number) + 1) + '. ';
            }

            if ((parsed.type === 'task' || parsed.type === 'bullet' || parsed.type === 'numbered') && parsed.content.trim() === '') {
                setLines(prev => {
                    const newLines = [...prev];
                    newLines[index] = '';
                    return newLines;
                });
                return;
            }

            setLines(prev => {
                const newLines = [...prev];
                newLines.splice(index + 1, 0, newLinePrefix);
                return newLines;
            });
            setActiveLineIndex(index + 1);
        }

        if (e.key === 'Backspace' && e.target.selectionStart === 0 && index > 0) {
            e.preventDefault();
            const currentLine = lines[index];
            setLines(prev => {
                const newLines = [...prev];
                newLines[index - 1] = newLines[index - 1] + currentLine;
                newLines.splice(index, 1);
                return newLines;
            });
            setActiveLineIndex(index - 1);
        }

        if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            setActiveLineIndex(index - 1);
        }
        if (e.key === 'ArrowDown' && index < lines.length - 1) {
            e.preventDefault();
            setActiveLineIndex(index + 1);
        }

        if (e.key === 'Tab') {
            e.preventDefault();
        }

        if (e.key === 'Escape') {
            if (showBlockMenu) {
                setShowBlockMenu(false);
            } else if (isExpanded) {
                setIsExpanded(false);
            } else {
                onClose();
            }
        }
    }, [lines, showBlockMenu, isExpanded, onClose]);

    const handleToggleTask = useCallback((index) => {
        setLines(prev => {
            const newLines = [...prev];
            const line = newLines[index];
            if (line.match(/^-\s*\[x\]\s*/i)) {
                newLines[index] = line.replace(/^-\s*\[x\]\s*/i, '- [ ] ');
            } else if (line.match(/^-\s*\[\s*\]\s*/)) {
                newLines[index] = line.replace(/^-\s*\[\s*\]\s*/, '- [x] ');
            }
            return newLines;
        });
    }, []);

    const handleBlockTypeSelect = useCallback((prefix) => {
        setLines(prev => {
            const newLines = [...prev];
            newLines[activeLineIndex] = prefix;
            return newLines;
        });
        setShowBlockMenu(false);
    }, [activeLineIndex]);

    const insertFormatting = useCallback((prefix, suffix = '') => {
        setLines(prev => {
            const newLines = [...prev];
            const currentLine = newLines[activeLineIndex] || '';
            newLines[activeLineIndex] = currentLine + prefix + suffix;
            return newLines;
        });
    }, [activeLineIndex]);

    const insertEmoji = useCallback((emoji) => {
        setLines(prev => {
            const newLines = [...prev];
            const currentLine = newLines[activeLineIndex] || '';
            newLines[activeLineIndex] = currentLine + emoji;
            return newLines;
        });
        setShowEmojiPicker(false);
    }, [activeLineIndex]);

    const handleSave = async () => {
        if (!content.trim()) {
            setError('Note content cannot be empty');
            return;
        }

        setError(null);
        try {
            await onSave({
                content: content.trim(),
                visibility,
                pinned,
                attachments: attachments.map(a => a.id),
            });
        } catch (err) {
            setError(err.message || 'Failed to save note');
        }
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const uploadPromises = files.map(file => uploadNoteAttachment(file));
            const results = await Promise.all(uploadPromises);
            const newAttachments = results.map(r => r.data);
            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (err) {
            console.error('Upload failed:', err);
            setError('Failed to upload file(s)');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeAttachment = (attachmentId) => {
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    };

    const selectedVisibility = VISIBILITY_OPTIONS.find(v => v.value === visibility);

    const toolbarItems = [
        { icon: Bold, action: () => insertFormatting('**', '**'), title: 'Bold' },
        { icon: Italic, action: () => insertFormatting('*', '*'), title: 'Italic' },
        { icon: Code, action: () => insertFormatting('`', '`'), title: 'Code' },
        { type: 'divider' },
        { icon: List, action: () => handleBlockTypeSelect('- '), title: 'Bullet list' },
        { icon: ListOrdered, action: () => handleBlockTypeSelect('1. '), title: 'Numbered list' },
        { icon: CheckSquare, action: () => handleBlockTypeSelect('- [ ] '), title: 'To-do' },
        { type: 'divider' },
        { icon: Link2, action: () => insertFormatting('[', '](url)'), title: 'Link' },
        { icon: Hash, action: () => insertFormatting('#'), title: 'Tag' },
        { icon: Smile, action: () => setShowEmojiPicker(!showEmojiPicker), title: 'Emoji', isEmojiButton: true },
        { type: 'divider' },
        { icon: Paperclip, action: () => fileInputRef.current?.click(), title: 'Attach file', loading: isUploading },
    ];

    return (
        <div
            className={`fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fadeIn ${isExpanded ? 'p-0' : 'p-4'}`}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className={`bg-[#0d1424] border border-white/10 shadow-2xl flex flex-col overflow-hidden transition-all duration-300 animate-slideUp ${
                    isExpanded
                        ? 'w-full h-full max-w-full max-h-full rounded-none'
                        : 'w-full max-w-3xl max-h-[90vh] rounded-2xl'
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white">
                        {note ? 'Edit Note' : 'New Note'}
                    </h2>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                            title={isExpanded ? 'Exit full screen' : 'Full screen'}
                        >
                            {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex items-center gap-0.5 px-4 py-2 border-b border-white/10 overflow-x-auto">
                    {toolbarItems.map((item, i) => {
                        if (item.type === 'divider') {
                            return <div key={i} className="w-px h-5 bg-white/10 mx-2" />;
                        }
                        if (item.isEmojiButton) {
                            return (
                                <div key={i} className="relative">
                                    <button
                                        onClick={item.action}
                                        className={`p-2 rounded-lg transition ${
                                            showEmojiPicker
                                                ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                                                : 'text-white/40 hover:text-white hover:bg-white/5'
                                        }`}
                                        title={item.title}
                                    >
                                        <item.icon size={16} />
                                    </button>
                                    {showEmojiPicker && (
                                        <EmojiPicker
                                            onSelect={insertEmoji}
                                            onClose={() => setShowEmojiPicker(false)}
                                        />
                                    )}
                                </div>
                            );
                        }
                        return (
                            <button
                                key={i}
                                onClick={item.action}
                                disabled={item.loading}
                                className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition disabled:opacity-50"
                                title={item.title}
                            >
                                {item.loading ? <Loader2 size={16} className="animate-spin" /> : <item.icon size={16} />}
                            </button>
                        );
                    })}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                        accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                    />
                </div>

                {/* Content Area */}
                <div
                    ref={editorRef}
                    className="flex-1 overflow-y-auto p-6 relative"
                    onClick={() => {
                        if (lines.length > 0) {
                            setActiveLineIndex(lines.length - 1);
                        }
                    }}
                >
                    <div className="max-w-2xl mx-auto space-y-0.5" onClick={(e) => e.stopPropagation()}>
                        {lines.map((line, index) => (
                            <EditableBlock
                                key={index}
                                line={line}
                                index={index}
                                isActive={activeLineIndex === index}
                                onFocus={setActiveLineIndex}
                                onChange={handleLineChange}
                                onKeyDown={handleKeyDown}
                                onToggleTask={handleToggleTask}
                                blockRef={el => blockRefs.current[index] = el}
                            />
                        ))}
                        <button
                            onClick={() => {
                                setLines(prev => [...prev, '']);
                                setActiveLineIndex(lines.length);
                            }}
                            className="flex items-center gap-2 py-2 px-2 -mx-2 text-white/30 hover:text-white/60 rounded-lg hover:bg-white/5 transition w-full text-left text-sm"
                        >
                            <Plus size={16} />
                            <span>Add a block</span>
                        </button>
                    </div>

                    {showBlockMenu && (
                        <BlockTypeMenu
                            position={blockMenuPosition}
                            onSelect={handleBlockTypeSelect}
                            onClose={() => setShowBlockMenu(false)}
                        />
                    )}
                </div>

                {/* Attachments */}
                {attachments.length > 0 && (
                    <div className="px-4 py-2 border-t border-white/10">
                        <div className="flex items-center gap-2 flex-wrap">
                            {attachments.map(att => (
                                <div
                                    key={att.id}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg text-sm"
                                >
                                    <Paperclip size={14} className="text-white/40" />
                                    <span className="text-white/70 truncate max-w-[150px]">
                                        {att.filename || att.name || 'Attachment'}
                                    </span>
                                    <button
                                        onClick={() => removeAttachment(att.id)}
                                        className="text-white/40 hover:text-red-500 transition"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
                        <div className="flex items-center gap-2 text-red-500 text-sm">
                            <AlertTriangle size={16} />
                            {error}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between p-4 border-t border-white/10 bg-[#0a0f1c]">
                    <div className="flex items-center gap-3">
                        {/* Pin Toggle */}
                        <button
                            onClick={() => setPinned(!pinned)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition ${pinned
                                ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                                : 'bg-white/5 text-white/50 hover:text-[#ff4d4d]'
                                }`}
                        >
                            <Pin size={14} className={pinned ? 'fill-current' : ''} />
                            {pinned ? 'Pinned' : 'Pin'}
                        </button>

                        {/* Visibility Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white/5 text-white/50 hover:text-white transition"
                            >
                                {selectedVisibility && <selectedVisibility.icon size={14} />}
                                {selectedVisibility?.label}
                                <ChevronDown size={14} />
                            </button>
                            {showVisibilityDropdown && (
                                <div className="absolute bottom-full left-0 mb-2 w-48 bg-[#0d1424] border border-white/10 rounded-lg shadow-xl overflow-hidden z-10">
                                    {VISIBILITY_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => {
                                                setVisibility(opt.value);
                                                setShowVisibilityDropdown(false);
                                            }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${visibility === opt.value
                                                ? 'bg-white/5'
                                                : 'hover:bg-white/5'
                                                }`}
                                        >
                                            <opt.icon size={14} className="text-white/50" />
                                            <div>
                                                <div className="text-white font-medium">{opt.label}</div>
                                                <div className="text-xs text-white/40">{opt.description}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Autosave Status */}
                        <div className="flex items-center gap-2 text-xs">
                            {saveStatus === 'saving' && (
                                <span className="flex items-center gap-1.5 text-[#ff4d4d]">
                                    <Loader2 size={14} className="animate-spin" />
                                    Saving...
                                </span>
                            )}
                            {saveStatus === 'saved' && (
                                <span className="flex items-center gap-1.5 text-emerald-500">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Saved
                                </span>
                            )}
                            {saveStatus === 'unsaved' && (
                                <span className="flex items-center gap-1.5 text-white/40">
                                    <div className="w-2 h-2 rounded-full bg-[#ff4d4d] animate-pulse" />
                                    Editing...
                                </span>
                            )}
                            {saveStatus === 'error' && (
                                <span className="flex items-center gap-1.5 text-red-500">
                                    <AlertTriangle size={14} />
                                    Save failed
                                </span>
                            )}
                        </div>

                        <span className="text-xs text-white/30 hidden sm:inline">
                            Auto-saves as you type
                        </span>

                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition shadow-lg shadow-red-900/20"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoteEditor;
